import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FileSizePipe,
  NotificationService,
  ResultWrapper,
} from '@seahorse/common';
import { FileModel, FileUpdateModel, FileService } from '@seahorse/domain';
import {
  AreYouSureModalComponent,
  LoadingIndicatorComponent,
} from '@seahorse/ui';
import { Subscription } from 'rxjs';
import { ModalService } from '../../modal/modal.service';
import { ModalAction, ModalResponse } from '../../modal/modal.model';

@Component({
  selector: 'temp-photo-gallery-modal',
  styleUrls: ['photo-gallery-modal.component.scss'],
  templateUrl: 'photo-gallery-modal.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FileSizePipe,
    LoadingIndicatorComponent,
  ],
})
export class PhotoGalleryModalComponent implements OnInit, OnDestroy {
  private _subscription = new Subscription();
  private _activeModal = inject(NgbActiveModal);
  private _modalService = inject(ModalService);
  private _translateService = inject(TranslateService);
  private _notificationService = inject(NotificationService);
  private _filesService = inject(FileService);

  @Input() title? = 'files.photoGalleryModal.title';
  @Input() files: FileModel[] = [];

  private _file: FileModel;
  @Input() set file(val: FileModel) {
    this._file = val;

    if (val) {
      if (!val.content) {
        this.loadFileContent();
      } else {
        this.setBlob();
      }
    }
  }
  get file(): FileModel {
    return this._file;
  }

  @Input() readonly = false;
  @Input() changePrivacyOption = true;
  @Input() alwaysShowRotateButtons = false;

  loading = false;
  canRotate = false;
  canZoom = false;
  zoomed = false;
  fileBlob: string;

  ngOnInit() {
    const fileIndex = this.files.findIndex((x) => x.id === this.file.id);
    if (fileIndex === -1) {
      this.files.push(this.file);
    }

    if (this.alwaysShowRotateButtons) {
      this.canRotate = true;
    } else {
      this._subscription.add(
        this._filesService.getPhotoExtensions().subscribe({
          next: (response) => {
            if (response.hasResult) {
              this.canRotate = response.result.includes(this.file.fileType);
            } else {
              this._notificationService.displayErrorNotification(
                response.messages
              );
            }
          },
          error: (e) => {
            this._notificationService.displayErrorNotification(e);
          },
        })
      );
    }
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
    URL.revokeObjectURL(this.fileBlob);
  }

  loadFileContent() {
    this.loading = true;
    this._subscription.add(
      this._filesService.getFileById(this.file.id).subscribe({
        next: (response) => {
          if (response.hasResult) {
            const fileIndex = this.files.findIndex(
              (x) => x.id === this.file.id
            );
            if (fileIndex !== -1) {
              this.files[fileIndex].content = response.result.content;
              this.file = this.files[fileIndex];
              this.setBlob();
            }
          } else {
            this._notificationService.displayErrorNotification(
              response.messages
            );
          }
        },
        error: (e) => {
          this._notificationService.displayErrorNotification(e);
        },
        complete: () => {
          this.loading = false;
        },
      })
    );
  }

  setBlob() {
    const content = this._filesService.base64ToArrayBuffer(this.file.content);
    const arrayBuffer =
      content.buffer instanceof ArrayBuffer
        ? content.buffer
        : new ArrayBuffer(content.length);

    this.fileBlob = URL.createObjectURL(
      new Blob([arrayBuffer], { type: this.file.mimeType })
    );
  }

  close(action: ModalAction = ModalAction.Confirm) {
    const modal: ModalResponse = {
      action,
      componentRef: {
        file: this.file,
        files: this.files,
      },
    };

    this._activeModal.close(modal);
  }

  goNext() {
    const currentIndex = this.files.findIndex((x) => x.id === this.file.id);
    if (currentIndex < this.files.length - 1) {
      this.file = this.files[currentIndex + 1];
    } else {
      this.file = this.files[0];
    }
  }

  goPrev() {
    const currentIndex = this.files.findIndex((x) => x.id === this.file.id);
    if (currentIndex > 0) {
      this.file = this.files[currentIndex - 1];
    } else {
      this.file = this.files[this.files.length - 1];
    }
  }

  fileDownload() {
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = this.fileBlob;
    link.download = this.file.name + this.file.fileType;
    link.click();
  }

  onDeleteClick() {
    this._subscription.add(
      this._modalService.openModal(AreYouSureModalComponent).subscribe((y) => {
        if (y) {
          this.fileDelete();
        }
      })
    );
  }

  fileDelete() {
    if (this.zoomed) {
      this.toggleZoom();
    }

    this.loading = true;
    this._subscription.add(
      this._filesService.deleteFile(this.file.id).subscribe({
        next: (response: ResultWrapper<FileModel>) => {
          if (response.hasResult) {
            this._notificationService.showSuccess(
              this._translateService.instant(
                'customModules.orderRegistrations.fileDeleted'
              ),
              this._translateService.instant('shared.terms.success')
            );
            const fileIndex = this.files.findIndex(
              (x) => x.id === this.file.id
            );
            this.files = this.files.filter((x) => x.id !== this.file.id);

            if (!this.files.length) {
              this.file = null;
              this.close(ModalAction.Delete);
            } else {
              let nextIndex = fileIndex;
              if (nextIndex >= this.files.length) {
                nextIndex = this.files.length - 1;
              }
              this.file = this.files[nextIndex];
            }
          } else {
            this._notificationService.displayErrorNotification(
              response.messages
            );
          }
        },
        error: (e) => {
          this._notificationService.displayErrorNotification(e);
        },
        complete: () => {
          this.loading = false;
        },
      })
    );
  }

  updatePrivacy() {
    if (this.zoomed) {
      this.toggleZoom();
    }

    this.file.isPrivate = !this.file.isPrivate;
    const fileUpdateModel = FileUpdateModel.fromFileModel(this.file);
    this._subscription.add(
      this._filesService.updateFile(fileUpdateModel).subscribe({
        next: (response) => {
          if (response.hasResult) {
            const fileIndex = this.files.findIndex(
              (x) => x.id === this.file.id
            );
            if (fileIndex !== -1) {
              this.files[fileIndex].isPrivate = response.result.isPrivate;
              this.file = this.files[fileIndex];
            }
          } else {
            this._notificationService.displayErrorNotification(
              response.messages
            );
          }

          if (this.files.length === 1) {
            this._notificationService.showSuccess(
              this._translateService.instant('files.model.changedPrivacy'),
              this._translateService.instant('shared.terms.success')
            );
            this.close();
          }
        },
        error: (e) => {
          this._notificationService.displayErrorNotification(e);
        },
      })
    );
  }

  rotate(antiClockwise: boolean = false) {
    if (this.zoomed) {
      this.toggleZoom();
    }

    this.loading = true;
    this._filesService.rotatePhoto(this.file.id, antiClockwise).subscribe({
      next: (response) => {
        if (response.hasResult) {
          const fileIndex = this.files.findIndex((x) => x.id === this.file.id);
          if (fileIndex !== -1) {
            this.files[fileIndex].content = response.result.content;
            this.files[fileIndex].thumbnail = response.result.thumbnail;
            this.file = this.files[fileIndex];
          }
        } else {
          this._notificationService.displayErrorNotification(response.messages);
        }
      },
      error: (e) => {
        this._notificationService.displayErrorNotification(e);
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  onImageLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    this.canZoom =
      img.naturalWidth > img.width || img.naturalHeight > img.height;
  }

  toggleZoom() {
    this.zoomed = !this.zoomed;
  }
}
